import React, { Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import { AnalyticsProvider } from './context/AnalyticsContext';
import { ShopProvider } from './context/ShopContext';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import { NavigationService } from './services/navigationService';
import Home from './pages/Home';
import About from './pages/About';
import Services from './pages/Services';
import ServiceDetails from './pages/ServiceDetails';
import BlogPost from './pages/BlogPost';
import BlogList from './pages/BlogList';
import ProductDetails from './pages/ProductDetails';
import SellerProfile from './pages/SellerProfile';
import ShopProfile from './pages/ShopProfile';
import Contact from './pages/Contact';
import CountyInfo from './pages/CountyInfo';
import NotFound from './pages/NotFound';
import Login from './pages/Login';
import Register from './pages/Register';
import Marketplace from './pages/Marketplace';
import CommunityHub from './pages/CommunityHub';
import ResetPassword from './pages/ResetPassword';
import DashboardLayout from './components/dashboard/DashboardLayout';
import AgriDashboard from './pages/AgriDashboard';
import ErrorBoundary from './components/ErrorBoundary';
import FarmDetails from './pages/farm/FarmDetails';
import SmartFarming from './pages/farm/SmartFarming';
import WeatherPage from './pages/farm/WeatherPage';
import ImportExportAnalysis from './pages/ImportExportAnalysis';
import ImportExportLab from './pages/ImportExportLab';
import TestPage from './pages/TestPage';
import TestSupabase from './pages/TestSupabase';
import EmailVerification from './pages/EmailVerification';
import CarbonCredits from './pages/CarbonCredits';
import Checkout from './pages/Checkout';
import OrderSuccess from './pages/OrderSuccess';
import OrderTracking from './pages/OrderTracking';
import UserDashboard from './pages/UserDashboard';
import PageTransition from './components/common/PageTransition';
import PostAuthReturnModal from './components/common/PostAuthReturnModal';
import { shopSlugUtils } from './services/shopVanityUrlService';

// Dashboard-only components loaded with React.lazy + Suspense to keep the initial
// bundle small for mobile (Capacitor/Tauri/Expo WebView cold starts matter).
// These route chunks are fetched only when the user navigates inside /dashboard/*
// — a service worker or native app asset preload can cache them offline afterwards.
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const MyFarms = React.lazy(() => import('./components/farm/MyFarms'));
const FarmCalculator = React.lazy(() => import('./components/farm/FarmCalculator'));
const Payment = React.lazy(() => import('./components/payment/Payment'));
const LogisticsCoordination = React.lazy(() => import('./components/logistics/LogisticsCoordination'));
const AnalyticsDashboard = React.lazy(() => import('./components/analytics/Analytics'));
const WorkerManagementPage = React.lazy(() => import('./pages/dashboard/WorkerManagementPage'));
const FarmTasksDashboardPage = React.lazy(() => import('./pages/dashboard/FarmTasksDashboardPage'));
const MyShops = React.lazy(() => import('./pages/dashboard/MyShops'));
const ManageProducts = React.lazy(() => import('./pages/dashboard/ManageProducts'));
const SellerOrders = React.lazy(() => import('./pages/dashboard/SellerOrders'));
const ShopSettings = React.lazy(() => import('./pages/dashboard/ShopSettings'));
const CampaignManager = React.lazy(() => import('./pages/dashboard/CampaignManager'));
const WealthCenter = React.lazy(() => import('./pages/dashboard/WealthCenter'));
const DealRoom = React.lazy(() => import('./pages/dashboard/DealRoom'));
const NotificationsPage = React.lazy(() => import('./pages/dashboard/NotificationsPage'));
const Inbox = React.lazy(() => import('./pages/dashboard/Inbox'));
const SubscriptionManagement = React.lazy(() => import('./pages/dashboard/SubscriptionManagement'));
const AdminPortal = React.lazy(() => import('./pages/dashboard/AdminPortal'));
const MyBuyerRequests = React.lazy(() => import('./pages/dashboard/MyBuyerRequests'));
const CareersHome = React.lazy(() => import('./pages/dashboard/careers/CareersHome'));
const FindWork = React.lazy(() => import('./pages/dashboard/careers/FindWork'));
const PostJob = React.lazy(() => import('./pages/dashboard/careers/PostJob'));
const JobDetail = React.lazy(() => import('./pages/dashboard/careers/JobDetail'));
const MarketPrediction = React.lazy(() => import('./components/market/MarketPrediction'));
const PestDetection = React.lazy(() => import('./components/pest/PestDetection'));
const PersonalizedRecommendations = React.lazy(() => import('./components/ai/PersonalizedRecommendations'));
const IoTDashboard = React.lazy(() => import('./pages/dashboard/IoTDashboard'));
const LivestockTracking = React.lazy(() => import('./pages/dashboard/LivestockTracking'));
const BlockchainTraceability = React.lazy(() => import('./pages/dashboard/BlockchainTraceability'));
const WeatherInsurance = React.lazy(() => import('./pages/dashboard/WeatherInsurance'));
const P2PLending = React.lazy(() => import('./pages/dashboard/P2PLending'));
const ColdChainMonitoring = React.lazy(() => import('./pages/dashboard/ColdChainMonitoring'));
const DroneMapping = React.lazy(() => import('./pages/dashboard/DroneMapping'));

// Lightweight loading UI used by Suspense while a lazy route chunk downloads.
// Spinner is Tailwind-only on purpose (zero JS deps) so even a cold WebView on a
// mid-tier Android/iOS device can render it without downloading MUI.
const RouteLoadingFallback: React.FC = () => (
  <div className="flex items-center justify-center min-h-[60vh] w-full">
    <div className="flex flex-col items-center gap-3">
      <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-green-500" />
      <div className="text-sm text-gray-500">Loading…</div>
    </div>
  </div>
);


/**
 * PageWrapper component that applies PageTransition to its children
 * Uses the current location key to trigger the transition on route changes
 */
const PageWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  return (
    <PageTransition key={location.pathname}>
      {children}
    </PageTransition>
  );
};

const ScrollToTop: React.FC = () => {
  const location = useLocation();

  React.useEffect(() => {
    if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [location.pathname, location.search]);

  return null;
};

/**
 * ProtectedRoute component that checks authentication status
 * Redirects to login page if user is not authenticated
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render if authenticated
 * @returns {JSX.Element} Either the protected children or a redirect to login
 */
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    if (typeof window !== 'undefined') {
      import('./services/authReturnService').then(({ saveAuthReturnRoute }) => {
        saveAuthReturnRoute(location);
      }).catch(() => {});
    }
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
};

const VanitySubdomainRedirector: React.FC = () => {
  const location = useLocation();
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const host = (window.location.hostname || '').trim();
    if (!host) return;
    if (/^localhost$|^127\.0\.0\.1$|^\[|^(www\.|app\.|staging\.|prod\.|admin\.)/i.test(host)) return;
    const parts = host.split('.');
    if (parts.length < 3) return;
    const subdomain = parts[0];
    const slug = shopSlugUtils.toSlug(subdomain || '');
    if (!slug) return;
    if (location.pathname && location.pathname !== '/' && location.pathname !== '') return;
    const port = window.location.port ? `:${window.location.port}` : '';
    const next = `${window.location.protocol}//${parts.slice(1).join('.')}${port}/shop/${encodeURIComponent(slug)}${window.location.search || ''}${window.location.hash || ''}`;
    window.location.replace(next);
  }, [location.pathname]);
  return null;
};

const RedirectRoot: React.FC = () => {
  return <Navigate to="/marketplace" replace />;
};

const DashboardRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <ProtectedRoute>
      <DashboardLayout>
        <Suspense fallback={<RouteLoadingFallback />}>{children}</Suspense>
      </DashboardLayout>
    </ProtectedRoute>
  );
};

/**
 * AppRoutes component that defines all application routes
 * Organizes routes into public and protected sections
 * Provides analytics context to the entire application
 * 
 * @returns {JSX.Element} Application routing configuration with providers
 */
const AppRoutes: React.FC = () => {
  const { loading } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Update navigation service with current path
    NavigationService.updateCurrentPath(location.pathname);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
      </div>
    );
  }

  console.log("AppRoutes component rendering");
  return (
    <AnalyticsProvider>
      <ShopProvider>
        <VanitySubdomainRedirector />
        <ScrollToTop />
        <div className="flex flex-col min-h-screen">
          <Navbar />
          <main className="flex-grow">
            <ErrorBoundary key={location.pathname}>
              <PageWrapper>
                <Routes>
                  {/* Public Routes - Accessible without authentication */}
                  <Route path="/" element={<RedirectRoot />} />
                  <Route path="/home" element={<Home />} />
                  <Route path="/about" element={<About />} />
                  <Route path="/services" element={<Services />} />
                  <Route path="/services/:serviceId" element={<ServiceDetails />} />
                  <Route path="/blog" element={<BlogList />} />
                  <Route path="/blog/:postId" element={<BlogPost />} />
                  <Route path="/products/:productId" element={<ProductDetails />} />
                  <Route path="/contact" element={<Contact />} />
                  <Route path="/counties" element={<CountyInfo />} />
                  <Route path="/marketplace" element={<Marketplace />} />
                  <Route path="/marketplace/product/:id" element={<ProductDetails />} />
                  <Route path="/marketplace/seller/:id" element={<SellerProfile />} />
                  <Route path="/shops/:shopSlug" element={<ShopProfile />} />
                  <Route path="/shop/:vanitySlug" element={<ShopProfile />} />
                  <Route path="/account" element={
                    <ProtectedRoute>
                      <UserDashboard />
                    </ProtectedRoute>
                  } />
                  <Route path="/checkout" element={
                    <ProtectedRoute>
                      <Checkout />
                    </ProtectedRoute>
                  } />
                  <Route path="/order-success" element={
                    <ProtectedRoute>
                      <OrderSuccess />
                    </ProtectedRoute>
                  } />
                  <Route path="/order-tracking/:orderId" element={
                    <ProtectedRoute>
                      <OrderTracking />
                    </ProtectedRoute>
                  } />
                  <Route path="/community" element={<CommunityHub />} />
                  {/* Convenience marketing URL: anyone landing at /careers is redirected to the dashboard careers experience */}
                  <Route path="/careers" element={<Navigate to="/dashboard/careers" replace />} />
                  {/* Remove the commented InfoSearch route entirely */}
                  <Route path="/test" element={<TestPage />} />
                  <Route path="/test-supabase" element={<TestSupabase />} />
                  <Route path="/agri-dashboard" element={
                    <ErrorBoundary>
                      <AgriDashboard />
                    </ErrorBoundary>
                  } />
                  <Route path="/import-export" element={
                    <ErrorBoundary>
                      <ImportExportAnalysis />
                    </ErrorBoundary>
                  } />
                  <Route path="/import-export-lab" element={
                    <ErrorBoundary>
                      <ImportExportLab />
                    </ErrorBoundary>
                  } />
                  <Route path="/login" element={<Login />} />
                  <Route path="/register" element={<Register />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/verify-email" element={<EmailVerification />} />

                  {/* Protected Dashboard Routes - Require authentication */}
                  <Route path="/dashboard" element={
                    <DashboardRoute>
                      <Dashboard />
                    </DashboardRoute>
                  } />
                  <Route path="/dashboard/my-shops" element={<DashboardRoute><MyShops /></DashboardRoute>} />
                  <Route path="/dashboard/my-shops/:shopId/settings" element={<DashboardRoute><ShopSettings /></DashboardRoute>} />
                  <Route path="/dashboard/my-shops/:shopId/campaigns" element={<DashboardRoute><CampaignManager /></DashboardRoute>} />
                  <Route path="/dashboard/my-shops/:shopId/wealth" element={<DashboardRoute><WealthCenter /></DashboardRoute>} />
                  <Route path="/dashboard/my-shops/:shopId/deals" element={<DashboardRoute><DealRoom /></DashboardRoute>} />
                  <Route path="/dashboard/notifications" element={<DashboardRoute><NotificationsPage /></DashboardRoute>} />
                  <Route path="/dashboard/inbox" element={<DashboardRoute><Inbox /></DashboardRoute>} />
                  <Route path="/dashboard/subscriptions" element={<DashboardRoute><SubscriptionManagement /></DashboardRoute>} />
                  <Route path="/dashboard/admin" element={<DashboardRoute><AdminPortal /></DashboardRoute>} />
                  <Route path="/dashboard/products" element={<DashboardRoute><ManageProducts /></DashboardRoute>} />
                  <Route path="/dashboard/orders" element={<DashboardRoute><SellerOrders /></DashboardRoute>} />
                  <Route path="/dashboard/my-farms" element={<DashboardRoute><MyFarms /></DashboardRoute>} />
                  <Route path="/dashboard/my-farms/:farmId/*" element={
                    <DashboardRoute>
                      <FarmDetails />
                    </DashboardRoute>
                  } />
                  <Route path="/dashboard/calculator" element={<DashboardRoute><FarmCalculator /></DashboardRoute>} />
                  <Route path="/dashboard/payment" element={<DashboardRoute><Payment /></DashboardRoute>} />
                  <Route path="/dashboard/marketplace" element={<DashboardRoute><Marketplace /></DashboardRoute>} />
                  <Route path="/dashboard/buyer-requests" element={<DashboardRoute><MyBuyerRequests /></DashboardRoute>} />
                  <Route path="/dashboard/community" element={<DashboardRoute><CommunityHub /></DashboardRoute>} />
                  <Route path="/dashboard/analytics" element={<DashboardRoute><AnalyticsDashboard /></DashboardRoute>} />
                  <Route path="/dashboard/logistics" element={<DashboardRoute><LogisticsCoordination /></DashboardRoute>} />
                  <Route path="/dashboard/carbon-credits" element={<DashboardRoute><CarbonCredits /></DashboardRoute>} />
                  <Route path="/dashboard/profile" element={<DashboardRoute><UserDashboard /></DashboardRoute>} />
                  <Route path="/dashboard/farm" element={<DashboardRoute><MyFarms /></DashboardRoute>} />
                  <Route path="/dashboard/smart-farming" element={<DashboardRoute><SmartFarming /></DashboardRoute>} />
                  <Route path="/dashboard/weather" element={<DashboardRoute><WeatherPage /></DashboardRoute>} />
                  <Route path="/dashboard/farm-tasks" element={<DashboardRoute><FarmTasksDashboardPage /></DashboardRoute>} />
                  <Route path="/dashboard/import-export" element={<DashboardRoute><ImportExportAnalysis /></DashboardRoute>} />
                  <Route path="/dashboard/workers" element={<DashboardRoute><WorkerManagementPage /></DashboardRoute>} />

                  {/* Careers & Farm Labor Marketplace */}
                  <Route path="/dashboard/careers" element={<DashboardRoute><CareersHome /></DashboardRoute>} />
                  <Route path="/dashboard/careers/find-work" element={<DashboardRoute><FindWork /></DashboardRoute>} />
                  <Route path="/dashboard/careers/post" element={<DashboardRoute><PostJob /></DashboardRoute>} />
                  <Route path="/dashboard/careers/post/:postingId" element={<DashboardRoute><PostJob /></DashboardRoute>} />
                  <Route path="/dashboard/careers/job/:postingId" element={<DashboardRoute><JobDetail /></DashboardRoute>} />

                  {/* AI & Intelligence Routes */}
                  <Route path="/dashboard/ai/market-predictions" element={<DashboardRoute><MarketPrediction /></DashboardRoute>} />
                  <Route path="/dashboard/ai/pest-detection" element={<DashboardRoute><PestDetection /></DashboardRoute>} />
                  <Route path="/dashboard/ai/recommendations" element={<DashboardRoute><PersonalizedRecommendations /></DashboardRoute>} />
                  <Route path="/dashboard/iot" element={<DashboardRoute><IoTDashboard /></DashboardRoute>} />
                  <Route path="/dashboard/livestock-tracking" element={<DashboardRoute><LivestockTracking /></DashboardRoute>} />
                  <Route path="/dashboard/traceability" element={<DashboardRoute><BlockchainTraceability /></DashboardRoute>} />
                  <Route path="/dashboard/insurance" element={<DashboardRoute><WeatherInsurance /></DashboardRoute>} />
                  <Route path="/dashboard/lending" element={<DashboardRoute><P2PLending /></DashboardRoute>} />
                  <Route path="/dashboard/cold-chain" element={<DashboardRoute><ColdChainMonitoring /></DashboardRoute>} />
                  <Route path="/dashboard/drone-mapping" element={<DashboardRoute><DroneMapping /></DashboardRoute>} />

                  {/* 404 Route - Catches all undefined routes */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </PageWrapper>
            </ErrorBoundary>
          </main>
          <Footer />
          <PostAuthReturnModal />
        </div>
      </ShopProvider>
    </AnalyticsProvider>
  );
};

export default AppRoutes;
